import React, { useMemo } from 'react';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  CompositeReportDefinitionSummary,
  Folder,
  ModelUtils,
  TemplateSummary,
} from '../../models';
import { useTemplatingItemSummaries } from '../../hooks/useTemplatingItemSummaries';
import { FileSystemTree } from '../FileSystemTree';
import { FolderButton } from '../Buttons/FolderButton';
import { useFolderState } from '../../hooks';
import { AnimatedFolderContent } from '../AnimatedFolderContent';
import { useDroppable } from '@dnd-kit/core';
import { Box } from '@lifeomic/chroma-react/components/Box';

const useStyles = makeStyles<{ level: number; isOver: boolean }>(theme => ({
  container: {
    backgroundColor: props =>
      props.isOver ? theme.palette.action.hover : 'transparent',
  },
  folderButtonContainer: {
    padding: theme.spacing(1),
    position: 'relative',
  },
  folderButton: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderRadius: 0,
    marginLeft: props => theme.spacing(props.level * 3),
  },
}));

interface FolderNodeProps {
  folder: Folder;
  folderNodeActions?: (data: Folder) => React.ReactNode;
  templateNodeActions?: (data: TemplateSummary) => React.ReactNode;
  compositeReportDefinitionNodeActions?: (
    data: CompositeReportDefinitionSummary,
  ) => React.ReactNode;
}

const FolderNode: React.FC<FolderNodeProps> = ({
  folder,
  folderNodeActions,
  templateNodeActions,
  compositeReportDefinitionNodeActions,
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: folder.path,
    data: {
      folder,
    },
  });
  const styles = useStyles({ level: ModelUtils.getLevel(folder), isOver });
  const { isExpanded, toggleFolderExpanded, expandFolder } = useFolderState({
    folder,
  });

  const { data: templatingItemSummaryPages } = useTemplatingItemSummaries({
    prefix: folder.path,
    enabled: isExpanded,
  });

  const templatingItemSummaries = useMemo(() => {
    return templatingItemSummaryPages?.pages.flatMap(_ => _);
  }, [templatingItemSummaryPages]);

  const handleClick = () => toggleFolderExpanded();
  const handleTemplatingItemCreate = () => expandFolder();

  return (
    <Box ref={setNodeRef} className={styles.container} direction="column">
      <Box className={styles.folderButtonContainer} direction="column">
        <FolderButton
          folder={folder}
          onClick={handleClick}
          isExpanded={isExpanded}
          className={styles.folderButton}
          onTemplatingItemCreate={handleTemplatingItemCreate}
          folderNodeActions={folderNodeActions}
        />
      </Box>
      <AnimatedFolderContent
        isExpanded={isExpanded && !!templatingItemSummaries}
      >
        {templatingItemSummaries && (
          <FileSystemTree
            templatingItemSummaries={templatingItemSummaries}
            folderNodeActions={folderNodeActions}
            templateNodeActions={templateNodeActions}
            compositeReportDefinitionNodeActions={
              compositeReportDefinitionNodeActions
            }
          />
        )}
      </AnimatedFolderContent>
    </Box>
  );
};

export { FolderNode };
